/**
 * Alpine components for the call surfaces:
 *   - incomingCallModal: the global ring modal (Accept / Decline).
 *   - inCallOverlay: the full-screen in-call surface (audio card or video).
 *   - threadCallButtons: the PM thread-header call affordance (button matrix).
 *
 * All three subscribe to the single call-state machine and call its methods.
 * Track attachment lives here because the overlay owns the <video>/<audio> refs.
 */

import { callState } from './call-state.js';
import { callApi, CallApiError } from './call-api.js';

const CALL_ACTIVE_STATUSES = ['ringing-out', 'connecting', 'active'];

function t(key, params) {
    return window.t ? window.t(key, params) : key;
}

function lucide(el) {
    if (window.LucideIcons) {
        window.LucideIcons.initialize(el);
    }
}

export function incomingCallModal() {
    return {
        visible: false,
        callId: '',
        type: 'audio',
        otherName: '',
        otherUsername: '',
        otherAvatar: '',
        _unsub: null,

        init() {
            this._unsub = callState.subscribe((s) => this.render(s));
            this.render(callState.snapshot());
        },

        destroy() {
            if (this._unsub) {
                this._unsub();
            }
        },

        render(s) {
            if (s.status === 'ringing-in') {
                this.callId = s.callId || '';
                this.type = s.type || 'audio';
                this.otherName = s.other?.name || s.other?.username || '';
                this.otherUsername = s.other?.username || '';
                this.otherAvatar = s.other?.avatar || '';
                if (!this.visible) {
                    this.visible = true;
                    this.$nextTick(() => lucide(this.$el));
                }
            } else if (this.visible) {
                this.visible = false;
            }
        },

        isVideo() {
            return this.type === 'video';
        },

        accept() {
            callState.accept();
        },

        decline() {
            callState.decline();
        },
    };
}

export function inCallOverlay() {
    return {
        visible: false,
        status: 'idle',
        type: 'audio',
        role: 'caller',
        otherId: '',
        otherName: '',
        otherUsername: '',
        otherAvatar: '',
        muted: false,
        cameraOff: false,
        hasRemoteVideo: false,
        hasLocalVideo: false,
        elapsed: 0,
        _timer: null,
        _startedAt: null,
        _unsub: null,
        _mediaUnsubs: [],
        _beforeUnload: null,

        init() {
            this._unsub = callState.subscribe((s) => this.render(s));
            this._mediaUnsubs.push(callState.on('remote-track', ({ track }) => this.attachRemote(track)));
            this._mediaUnsubs.push(callState.on('local-video-track', ({ track }) => this.attachLocal(track)));
            this._mediaUnsubs.push(callState.on('track-removed', ({ track }) => this.detach(track)));
            this.render(callState.snapshot());
        },

        destroy() {
            if (this._unsub) {
                this._unsub();
            }
            this._mediaUnsubs.forEach((fn) => fn && fn());
            this.stopTimer();
            this.removeUnloadGuard();
        },

        render(s) {
            const active = CALL_ACTIVE_STATUSES.includes(s.status);
            this.status = s.status;
            this.type = s.type || 'audio';
            this.role = s.role || 'caller';
            this.muted = s.muted;
            this.cameraOff = s.cameraOff;
            if (s.other) {
                this.otherId = s.other.id || '';
                this.otherName = s.other.name || s.other.username || '';
                this.otherUsername = s.other.username || '';
                this.otherAvatar = s.other.avatar || '';
            }

            if (active && !this.visible) {
                this.visible = true;
                this.addUnloadGuard();
            } else if (!active && this.visible) {
                this.visible = false;
                this.removeUnloadGuard();
                this.hasRemoteVideo = false;
                this.hasLocalVideo = false;
                this.elapsed = 0;
            }

            if (s.status === 'active' && !this._timer) {
                this.startTimer(s.startedAt);
            } else if (s.status !== 'active' && this._timer) {
                this.stopTimer();
            }

            if (active) {
                this.$nextTick(() => lucide(this.$el));
            }
        },

        // --- media attachment ---
        attachRemote(track) {
            if (!track) {
                return;
            }
            if (track.kind === 'video') {
                track.attach(this.$refs.remoteVideo);
                this.hasRemoteVideo = true;
            } else if (track.kind === 'audio') {
                track.attach(this.$refs.remoteAudio);
            }
        },

        attachLocal(track) {
            if (track && this.$refs.localVideo) {
                track.attach(this.$refs.localVideo);
                this.hasLocalVideo = true;
            }
        },

        detach(track) {
            if (!track) {
                return;
            }
            try {
                track.detach();
            } catch (e) {
                /* ignore */
            }
            if (track.kind === 'video') {
                this.hasRemoteVideo = false;
            }
        },

        // --- timer ---
        startTimer(startedAt) {
            this._startedAt = startedAt || Date.now();
            this.tick();
            this._timer = setInterval(() => this.tick(), 500);
        },

        tick() {
            this.elapsed = Math.max(0, Math.round((Date.now() - this._startedAt) / 1000));
        },

        stopTimer() {
            if (this._timer) {
                clearInterval(this._timer);
                this._timer = null;
            }
        },

        timerText() {
            const total = this.elapsed;
            const minutes = Math.floor(total / 60);
            const seconds = String(total % 60).padStart(2, '0');
            return `${minutes}:${seconds}`;
        },

        statusText() {
            if (this.status === 'active') {
                return this.timerText();
            }
            if (this.status === 'connecting') {
                return t('common.calls_connecting');
            }
            return t('common.calls_calling');
        },

        // --- unload guard ---
        addUnloadGuard() {
            if (this._beforeUnload) {
                return;
            }
            this._beforeUnload = (e) => {
                e.preventDefault();
                e.returnValue = '';
                return '';
            };
            window.addEventListener('beforeunload', this._beforeUnload);
        },

        removeUnloadGuard() {
            if (this._beforeUnload) {
                window.removeEventListener('beforeunload', this._beforeUnload);
                this._beforeUnload = null;
            }
        },

        // --- controls ---
        isVideo() {
            return this.type === 'video';
        },

        showRemoteVideoStage() {
            return this.isVideo() && this.status === 'active' && this.hasRemoteVideo;
        },

        toggleMute() {
            callState.toggleMute();
        },

        toggleCamera() {
            callState.toggleCamera();
        },

        switchCamera() {
            callState.switchCamera();
        },

        canSwitchCamera() {
            return this.isVideo() && callState.canSwitchCamera();
        },

        end() {
            callState.hangUp();
        },

        reportAndEnd() {
            const id = this.otherId;
            callState.hangUp();
            if (id && window.openReportModal) {
                window.openReportModal('user', id, id);
            }
        },
    };
}

export function threadCallButtons() {
    return {
        active: false,
        uid: '',
        username: '',
        name: '',
        avatar: '',
        selfUid: '',
        loading: true,
        canCall: false,
        authStatus: 'none',
        requestedBy: null,
        busy: false,
        _unsubs: [],

        init() {
            this.selfUid = document.querySelector('meta[name="user-id"]')?.content || '';

            const onOpen = (e) => this.setContact(e.detail || {});
            const onClose = () => {
                this.active = false;
                this.syncRevokeItem();
            };
            const onAuth = (e) => {
                const from = e.detail && e.detail.userId ? String(e.detail.userId) : null;
                if (this.active && (!from || from === this.uid)) {
                    this.load();
                }
            };
            const onChanged = (e) => {
                const uid = e.detail && e.detail.userId ? String(e.detail.userId) : null;
                if (this.active && (!uid || uid === this.uid)) {
                    this.load();
                }
            };
            document.addEventListener('pm:thread-opened', onOpen);
            document.addEventListener('pm:thread-closed', onClose);
            document.addEventListener('ws:call-auth-accepted', onAuth);
            document.addEventListener('ws:call-auth-request', onAuth);
            document.addEventListener('calls:authorization-changed', onChanged);
            this._unsubs.push(() => document.removeEventListener('pm:thread-opened', onOpen));
            this._unsubs.push(() => document.removeEventListener('pm:thread-closed', onClose));
            this._unsubs.push(() => document.removeEventListener('ws:call-auth-accepted', onAuth));
            this._unsubs.push(() => document.removeEventListener('ws:call-auth-request', onAuth));
            this._unsubs.push(() => document.removeEventListener('calls:authorization-changed', onChanged));

            // A thread can already be open on initial page load (deep link to
            // /app/messages?thread_id=...), where pm:thread-opened has not fired.
            this.detectActiveThread();
        },

        detectActiveThread() {
            const row = document.querySelector('#threads_left .pm_thread.active');
            if (row) {
                this.setContact({
                    uid: row.getAttribute('data-uid') || '',
                    username: row.getAttribute('data-username') || '',
                    name: row.getAttribute('data-name') || '',
                    avatar: row.getAttribute('data-thumb') || '',
                });
            }
        },

        destroy() {
            this._unsubs.forEach((fn) => fn && fn());
        },

        setContact({ uid, username, name, avatar }) {
            const nextUid = String(uid || '');
            if (!nextUid) {
                this.active = false;
                return;
            }
            this.uid = nextUid;
            this.username = username || '';
            this.name = name || username || '';
            this.avatar = avatar || '';
            this.active = true;
            this.load();
        },

        async load() {
            if (!this.uid) {
                this.loading = false;
                return;
            }
            try {
                const info = await callApi.getAuthorization(this.uid);
                this.canCall = !!info.can_call;
                this.authStatus = info.authorization?.status || 'none';
                this.requestedBy = info.authorization?.requested_by
                    ? String(info.authorization.requested_by)
                    : null;
            } catch (e) {
                this.canCall = false;
                this.authStatus = 'none';
            } finally {
                this.loading = false;
                this.syncRevokeItem();
                this.$nextTick(() => lucide(this.$el));
            }
        },

        // The revoke control lives in the conversation-header options menu
        // (outside this component), so its visibility is synced imperatively to
        // the authorized state.
        syncRevokeItem() {
            const item = document.getElementById('pm-call-revoke-item');
            if (!item) {
                return;
            }
            const show = this.active && this.authStatus === 'authorized';
            item.classList.toggle('hidden', !show);
            item.classList.toggle('block', show);
        },

        /**
         * The button-matrix state:
         *   inbound  - the other member asked to call me (accept / decline)
         *   call     - fully callable (authorized + every gate passes)
         *   sent     - my request is pending
         *   request  - callable pending authorization (offer to request)
         *   hidden   - nothing to show
         */
        matrix() {
            if (this.loading) {
                return 'loading';
            }
            if (this.authStatus === 'requested' && this.requestedBy && this.requestedBy === this.uid) {
                return 'inbound';
            }
            if (this.canCall) {
                return 'call';
            }
            if (this.authStatus === 'requested' && this.requestedBy === this.selfUid) {
                return 'sent';
            }
            if (this.authStatus === 'authorized') {
                return 'hidden';
            }
            return 'request';
        },

        startAudio() {
            this.start('audio');
        },

        startVideo() {
            this.start('video');
        },

        async start(type) {
            if (this.busy || !window.InterpalsCalls) {
                return;
            }
            this.busy = true;
            try {
                await window.InterpalsCalls.startCall(this.uid, type, {
                    id: this.uid,
                    username: this.username,
                    name: this.name,
                    avatar: this.avatar,
                });
            } catch (error) {
                this.handleError(error);
            } finally {
                this.busy = false;
            }
        },

        async requestCall() {
            if (this.busy) {
                return;
            }
            this.busy = true;
            try {
                await callApi.requestAuthorization(this.uid);
                this.authStatus = 'requested';
                this.requestedBy = this.selfUid;
                window.showSuccess?.(t('common.calls_toast_request_sent'));
            } catch (error) {
                this.handleError(error);
            } finally {
                this.busy = false;
                this.$nextTick(() => lucide(this.$el));
            }
        },

        async acceptInbound() {
            if (this.busy) {
                return;
            }
            this.busy = true;
            try {
                await callApi.acceptAuthorization(this.uid);
                this.authStatus = 'authorized';
                this.canCall = true;
            } catch (error) {
                this.handleError(error);
            } finally {
                this.busy = false;
                this.$nextTick(() => lucide(this.$el));
            }
        },

        async declineInbound() {
            if (this.busy) {
                return;
            }
            this.busy = true;
            try {
                await callApi.declineAuthorization(this.uid);
                this.authStatus = 'none';
                this.requestedBy = null;
            } catch (error) {
                this.handleError(error);
            } finally {
                this.busy = false;
                this.$nextTick(() => lucide(this.$el));
            }
        },

        handleError(error) {
            const code = error instanceof CallApiError ? error.code : '';
            const key = `common.calls_error_${code || 'unavailable'}`;
            const translated = t(key, { username: this.name || this.username });
            // Fall back to a generic message when a specific code has no copy.
            const message = translated === key
                ? t('common.calls_error_unavailable', { username: this.name || this.username })
                : translated;
            window.showError?.(message);

            // Codes that mean the affordance is now stale: re-fetch so the button
            // reflects the true state (authorization revoked, target busy, ...).
            if (code === 'call_authorization_required' || code === 'call_busy' || code === 'call_invalid_state') {
                this.load();
            }
        },
    };
}

export default { incomingCallModal, inCallOverlay, threadCallButtons };
